import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState } from "react";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AnswerList from "./pages/AnswerList";
import Answer from "./pages/Answer";
import View from "./pages/View";
import Policy from "./pages/Policy";
import Privacy from "./pages/Privacy";
import Agreement from "./pages/Agreement";
import AgreeRetroActive from "./pages/AgreeRetroactive";
import BarExamList from "./pages/BarExamList";
import BarExamAnswer from "./pages/BarExamAnswer";
import BarExamResult from "./pages/BarExamResult";
import BarExamStatistics from "./pages/BarExamStatistics";
import { hasAuthenticatedSession, SESSION_EXPIRED_EVENT } from "./api";
import { trackPageView } from "./analytics";
import RouteMetadata from "./components/RouteMetadata";
import Guide from "./pages/Guide";
import NotFound from "./pages/NotFound";

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

const PrivateRoute = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const auth = await hasAuthenticatedSession();
      setAuthenticated(auth);
    };

    checkAuth();
  }, []);

  if (authenticated === null) {
    return <div>Loading...</div>; // 인증 상태 확인 중일 때 로딩 표시
  }

  return authenticated ? children : <Navigate to="/login" />;
};

// 신규 → 저장된 답안으로 전환해도 같은 컴포넌트 트리를 유지한다.
// 서로 다른 인증 래퍼를 사용하면 Answer/Controls가 재생성되어 저장 완료창이 사라진다.
const AnswerRoute = ({ children, allowAnonymous = false }) => {
  const hasStoredSession = Boolean(
    localStorage.getItem("access_token") ||
    localStorage.getItem("refresh_token"),
  );
  const [authenticated, setAuthenticated] = useState(
    hasStoredSession ? null : false,
  );

  useEffect(() => {
    let active = true;
    if (!hasStoredSession) {
      setAuthenticated(false);
      return;
    }

    hasAuthenticatedSession()
      .then((result) => {
        if (active) setAuthenticated(result);
      })
      .catch(() => {
        if (active) setAuthenticated(false);
      });
    return () => {
      active = false;
    };
  }, [hasStoredSession]);

  if (authenticated === null) return <div>Loading...</div>;
  return authenticated || allowAnonymous ? (
    children
  ) : (
    <Navigate to="/login" replace />
  );
};

const SessionExpiryHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleSessionExpired = () => {
      navigate("/login", {
        replace: true,
        state: {
          sessionExpired: true,
          returnTo: `${location.pathname}${location.search}${location.hash}`,
          returnState: location.state,
        },
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [location, navigate]);

  return null;
};

const App = () => {
  return (
    <Router>
      <PageViewTracker />
      <RouteMetadata />
      <SessionExpiryHandler />
      <Routes>
        {/* Public Routes */}
        <Route path="/policy" element={<Policy />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/agreement_retroactive" element={<AgreeRetroActive />} />

        <Route path="/login" element={<Login />} />
        <Route path="/agreement" element={<Agreement />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/answers/new"
          element={
            <AnswerRoute allowAnonymous>
              <Answer />
            </AnswerRoute>
          }
        />

        {/* Public full-service routes */}
        <Route path="/bar-exam" element={<BarExamList />} />
        <Route path="/bar-exam/:examId/answer" element={<BarExamAnswer />} />
        <Route
          path="/bar-exam/:examId/statistics"
          element={<BarExamStatistics />}
        />

        {/* Protected Routes */}
        <Route
          path="/answers"
          element={
            <PrivateRoute>
              <AnswerList />
            </PrivateRoute>
          }
        />

        <Route
          path="/answers/:id"
          element={
            <AnswerRoute>
              <Answer />
            </AnswerRoute>
          }
        />
        <Route
          path="/bar-exam/result"
          element={
            <PrivateRoute>
              <BarExamResult />
            </PrivateRoute>
          }
        />
        <Route
          path="/view/:id"
          element={<View />} // Assuming view is public
        />

        <Route path="/" element={<BarExamList isHome />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default App;
